// A faint horizon of the place the language comes from, along the foot of the home.
//
// The Great Wall for Mandarin, Fuji and a torii for Japanese, London for English — keyed to the
// language being LEARNED, the same question the wallpaper and the title answer. Drawn as a layered
// scene rather than a flat cut-out: `currentColor` (the theme's own hue, set on the element) at
// several opacities gives depth, and `var(--bg)` paints the few highlights that should read as the
// page showing through — Fuji's snow, a clock face. So every theme writes its own horizon and a
// dark one gets a soft glowing ridge rather than a black slab, with no literal colour anywhere
// except the sakura, which is pink because that is the whole point of it.

const T = 'currentColor';

/** The stage every horizon is drawn on; wide and short, it sits along the foot of the page. */
export const W = 1440;
export const H = 300;

/** A pagoda of `tiers` eaves with upswept corners — the shape Mandarin and Japanese share. */
function pagoda(cx: number, base: number, w: number, tiers: number): string {
  let s = '';
  let y = base;
  let width = w;
  for (let i = 0; i < tiers; i++) {
    const h = width / 2;
    s +=
      `<path d="M${cx - h - 6} ${y} Q ${cx - h * 0.4} ${y - 11} ${cx} ${y - 13} ` +
      `Q ${cx + h * 0.4} ${y - 11} ${cx + h + 6} ${y} Q ${cx + h * 0.5} ${y - 4} ${cx} ${y - 4} ` +
      `Q ${cx - h * 0.5} ${y - 4} ${cx - h - 6} ${y} Z"/>` +
      `<rect x="${cx - h * 0.5}" y="${y}" width="${h}" height="13"/>`;
    y -= 19;
    width *= 0.82;
  }
  return s + `<rect x="${cx - 1.8}" y="${y - 12}" width="3.6" height="14"/>`;
}

/** A crenellated watchtower, a lighter doorway cut into it so it reads as built, not solid. */
function tower(x: number, top: number, base: number, w: number): string {
  const m = w / 5;
  let c = '';
  for (let i = 0; i < 5; i += 2) c += `<rect x="${x + i * m}" y="${top - 8}" width="${m}" height="8"/>`;
  return (
    `<rect x="${x}" y="${top}" width="${w}" height="${base - top}"/>` +
    c +
    `<rect x="${x + w * 0.34}" y="${top + 14}" width="${w * 0.32}" height="${base - top - 14}" fill="var(--bg)" opacity="0.5"/>`
  );
}

/** A length of wall climbing a crest, a ridge of merlons stepping along its back. */
function wall(pts: [number, number][]): string {
  const line = pts.map((p) => p.join(' ')).join(' L ');
  let c = '';
  for (let i = 0; i < pts.length - 1; i++) {
    const [x1, y1] = pts[i]!;
    const [x2, y2] = pts[i + 1]!;
    const steps = Math.max(2, Math.round((x2 - x1) / 24));
    for (let s = 0; s < steps; s++) {
      const t = s / steps;
      c += `<rect x="${x1 + (x2 - x1) * t}" y="${y1 + (y2 - y1) * t - 21}" width="10" height="10"/>`;
    }
  }
  return `<path fill="none" stroke="${T}" stroke-width="15" stroke-linejoin="round" d="M ${line}"/>` + c;
}

/** A distant flock, a few gulls at a glance. */
function birds(list: [number, number, number][]): string {
  return list
    .map(
      ([x, y, s]) =>
        `<g transform="translate(${x} ${y}) scale(${s})" opacity="0.5">` +
        `<path d="M-7 0 Q 0 -5.5 7 0 M7 0 Q 14 -5.5 21 0" fill="none" stroke="${T}" stroke-width="2.2" stroke-linecap="round"/></g>`,
    )
    .join('');
}

/** Sakura: soft pink petals, each on its own slow float-and-spin (held still by reduced-motion). */
function sakura(): string {
  const spots: [number, number, number, number, number][] = [
    [140, 70, 1.1, 8, 0], [300, 40, 0.8, 10, -3], [470, 96, 1.3, 9, -6],
    [820, 60, 0.9, 11, -1], [980, 110, 1.1, 7, -4], [1140, 50, 0.8, 10, -8],
    [1300, 96, 1.2, 9, -2], [640, 128, 0.7, 8, -5], [380, 150, 1.0, 12, -7],
  ];
  const petal =
    `<path d="M0 -6 C -4.6 -5 -4.6 3.4 0 6.2 C 4.6 3.4 4.6 -5 0 -6 Z" fill="#f3b4c6"/>` +
    `<path d="M0 -6 C -2 -5 -2 0 0 2 C 2 0 2 -5 0 -6 Z" fill="#ffffff" opacity="0.35"/>`;
  return spots
    .map(
      ([x, y, s, dur, delay]) =>
        `<g transform="translate(${x} ${y}) scale(${s})">` +
        `<g class="petal" style="animation-duration:${dur}s;animation-delay:${delay}s">${petal}</g></g>`,
    )
    .join('');
}

const CHINA =
  `<circle cx="1150" cy="138" r="84" fill="${T}" opacity="0.07"/>` +
  `<path d="M0 232 L170 168 L300 214 L440 156 L600 216 L780 158 L980 210 L1180 162 L1440 214 L1440 300 0 300Z" fill="${T}" opacity="0.1"/>` +
  `<path d="M0 254 L210 206 L380 248 L560 200 L770 252 L1000 206 L1210 250 L1440 210 L1440 300 0 300Z" fill="${T}" opacity="0.18"/>` +
  `<g fill="${T}" opacity="0.5">${wall([[36, 210], [210, 150], [392, 198], [566, 150], [760, 196], [980, 150], [1190, 198]])}</g>` +
  `<g fill="${T}" opacity="0.6">${tower(196, 120, 206, 52)}${tower(548, 120, 206, 54)}${tower(962, 120, 206, 52)}</g>` +
  `<g fill="${T}" opacity="0.55">${pagoda(1300, 272, 96, 3)}</g>` +
  birds([[260, 70, 1.2], [320, 86, 0.9], [1000, 96, 1.1], [1060, 80, 0.8]]) +
  `<path d="M0 276 C 360 270 720 274 1080 276 C 1260 277 1360 275 1440 276 L1440 300 0 300Z" fill="${T}" opacity="0.28"/>`;

const JAPAN =
  `<circle cx="600" cy="168" r="150" fill="${T}" opacity="0.08"/>` +
  `<path d="M0 252 C 300 232 520 244 760 252 C 1000 259 1220 240 1440 250 L1440 300 0 300Z" fill="${T}" opacity="0.12"/>` +
  `<ellipse cx="300" cy="196" rx="120" ry="12" fill="${T}" opacity="0.08"/>` +
  `<ellipse cx="1040" cy="176" rx="150" ry="13" fill="${T}" opacity="0.07"/>` +
  `<path d="M258 276 C 402 266 470 250 548 150 C 566 124 580 114 600 112 C 620 114 634 124 652 150 C 730 250 798 266 942 276 Z" fill="${T}" opacity="0.52"/>` +
  `<path d="M548 150 C 566 124 580 114 600 112 C 620 114 634 124 652 150 L644 154 L636 147 L627 156 L618 148 L609 157 L600 150 L591 158 L582 149 L573 156 L564 148 L556 155 Z" fill="var(--bg)"/>` +
  `<g fill="${T}" opacity="0.72">` +
  `<path d="M132 276 L139 156 L151 156 L158 276 Z"/><path d="M242 276 L249 156 L261 156 L268 276 Z"/>` +
  `<path d="M104 150 Q 120 140 152 142 Q 200 137 248 142 Q 280 140 296 150 L290 161 Q 200 150 110 161 Z"/>` +
  `<rect x="118" y="168" width="164" height="8" rx="2"/><rect x="192" y="152" width="16" height="18" rx="2"/></g>` +
  `<g fill="${T}" opacity="0.6">${pagoda(1230, 276, 92, 5)}</g>` +
  `<path d="M0 278 C 340 272 700 276 1040 278 C 1220 279 1340 276 1440 278 L1440 300 0 300Z" fill="${T}" opacity="0.3"/>` +
  sakura();

const ENGLISH =
  `<path d="M0 238 L120 238 L120 206 L190 206 L190 238 L300 238 L300 192 L360 192 L360 238 L1440 238 L1440 300 0 300Z" fill="${T}" opacity="0.1"/>` +
  `<g opacity="0.34" fill="none" stroke="${T}" stroke-width="3"><circle cx="250" cy="170" r="74"/><circle cx="250" cy="170" r="10"/>` +
  Array.from({ length: 12 }, (_, i) => {
    const a = (i * Math.PI) / 6;
    return `<line x1="250" y1="170" x2="${250 + 74 * Math.cos(a)}" y2="${170 + 74 * Math.sin(a)}"/>`;
  }).join('') +
  `</g><rect x="246" y="170" width="8" height="78" fill="${T}" opacity="0.3"/>` +
  `<g fill="${T}" opacity="0.42"><path d="M470 250 L470 196 Q520 150 570 196 L570 250 Z"/><ellipse cx="520" cy="196" rx="52" ry="30"/><rect x="514" y="150" width="12" height="20"/><circle cx="520" cy="146" r="5"/></g>` +
  `<g fill="${T}" opacity="0.46"><rect x="700" y="150" width="34" height="100"/><rect x="866" y="150" width="34" height="100"/><path d="M696 150 L717 120 L738 150 Z"/><path d="M862 150 L883 120 L904 150 Z"/><rect x="690" y="186" width="220" height="12"/></g>` +
  `<path d="M660 198 Q 800 150 940 198" fill="none" stroke="${T}" stroke-width="6" opacity="0.4"/>` +
  `<g fill="${T}" opacity="0.5"><rect x="1030" y="120" width="60" height="130"/><rect x="1040" y="140" width="40" height="40"/><circle cx="1060" cy="160" r="13" fill="var(--bg)" opacity="0.6"/><path d="M1026 120 L1060 72 L1094 120 Z"/><rect x="1056" y="56" width="8" height="18"/></g>` +
  birds([[380, 64, 1.2], [440, 80, 0.9], [1180, 84, 1.1], [1230, 66, 0.85]]) +
  `<path d="M0 250 L1440 250 L1440 300 0 300Z" fill="${T}" opacity="0.3"/>`;

const HORIZONS: Record<string, string> = { zh: CHINA, ja: JAPAN, en: ENGLISH };

/**
 * The horizon for the language being learned, as an `<svg>` ready to drop into the home — or null
 * for a language this build has no landmarks for, which simply gets no horizon.
 */
export function homeHorizon(code: string): SVGSVGElement | null {
  const scene = HORIZONS[code.split('-')[0] ?? ''];
  if (!scene) return null;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
  svg.setAttribute('preserveAspectRatio', 'xMidYMax meet');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('class', 'home-horizon');
  svg.innerHTML = scene;
  return svg;
}
